/**
 * In-house reactive localStorage: same API as reactive-localstorage (getItem, setItem, removeItem, clear, on('change')).
 * Uses native localStorage; emits 'change' on same-tab writes and on cross-tab storage events.
 */

type ChangeListener = (key: string | null, value: string | null, oldValue?: string | null) => void;

function createReactiveLocalStorage(): {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  clear: () => void;
  on: (event: string, fn: ChangeListener) => void;
  off: (event: string, fn: ChangeListener) => void;
} {
  const native =
    typeof window !== "undefined" ? window.localStorage : (null as unknown as Storage);
  const listeners: Record<string, Set<ChangeListener>> = { change: new Set() };

  function emitChange(key: string | null, value: string | null, oldValue?: string | null) {
    listeners.change.forEach((fn) => {
      try {
        fn(key, value, oldValue);
      } catch (e) {
        console.error(e);
      }
    });
  }

  if (native && typeof window !== "undefined") {
    window.addEventListener("storage", (e: StorageEvent) => {
      if (e.storageArea === native) {
        emitChange(e.key, e.newValue, e.oldValue);
      }
    });
  }

  return {
    getItem(key: string): string | null {
      if (!native) return null;
      return native.getItem(key);
    },

    setItem(key: string, value: string): void {
      if (!native) return;
      const oldValue = native.getItem(key);
      native.setItem(key, String(value));
      if (oldValue !== value) {
        emitChange(key, String(value), oldValue);
      }
    },

    removeItem(key: string): void {
      if (!native) return;
      const oldValue = native.getItem(key);
      native.removeItem(key);
      emitChange(key, null, oldValue);
    },

    clear(): void {
      if (!native) return;
      native.clear();
      emitChange(null, null, null);
    },

    on(event: string, fn: ChangeListener): void {
      if (event === "change") {
        listeners.change.add(fn);
      }
    },

    off(event: string, fn: ChangeListener): void {
      if (event === "change") {
        listeners.change.delete(fn);
      }
    },
  };
}

export const localStorage = createReactiveLocalStorage();
